<template>
    <div>
        <slot name="title">
            <h4 class="dinlineb mt-0">{{ $l10n.__('Smart-Tags') }}</h4>
        </slot>

        <div v-if="!loader && !editMode && display == 'column'" class="dinlineb">
            <link-button
                :permissions="permissions"
                :perm-to-check="permToCheck"
                @clicked="toggleMode"
            >
                {{ values.length > 0 ? $l10n.__('Modifier') : $l10n.__('Ajouter') }}
            </link-button>
        </div>

        <template v-loading="loader">
            <div v-if="editMode" class="prelative wp-10" :class="containerTagClass">
                <el-select
                    v-model="values"
                    multiple
                    filterable
                    :allow-create="permissions['can_create']"
                    default-first-option
                    :collapse-tags="false"
                    :placeholder="$t.__('utilities.Tags.enter_smarttag')"
                    :class="selectorContainerClass"
                    remote
                    :remote-method="remote"
                    :size="size"
                    data-bot="smart-tag__input"
                    @change="update"
                >
                    <el-option
                        v-for="item in tags"
                        :key="item.id"
                        :label="item.value"
                        :value="item.id"
                    >
                    </el-option>
                </el-select>

                <div v-if="standalone && isInline" class="flex-row ai-center">
                    <CompassButton
                        label="Enregistrer"
                        class="ml-2"
                        data-bot="smart-tags__save"
                        @click="record"
                    />
                    <CompassIconButton
                        icon="close"
                        class="ml-2"
                        data-bot="smart-tags__close"
                        @click="cancel"
                    />
                </div>

                <div v-else-if="standalone" class="aright mt-2">
                    <ul class="list-inline">
                        <li>
                            <a href="javascript:;" class="link gray-60" @click="cancel">
                                {{ $l10n.__('Annuler') }}
                            </a>
                        </li>
                        <li>
                            <a href="javascript:;" class="link" @click="record">
                                {{ $l10n.__('Valider') }}
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Display all tags as inline items list-->
            <div v-else class="label-tag flex-row" :class="labelTagContainerClass">
                <div v-if="!loader && tagsStore.length > 0" @click="editInPlace">
                    <CompassTooltip
                        v-for="(tag, index) in tagsStore"
                        :key="index"
                        ref="tags"
                        :content="tag.value"
                        placement="top"
                        content-class="label-tag--tooltip__container"
                    >
                        <span
                            class="label ellipsis"
                            :class="labelTagClass"
                            :data-bot="`smart-tags__tag-${index}`"
                        >
                            {{ tag.value }}
                        </span>
                    </CompassTooltip>
                </div>

                <!-- Only available if prop display is inline -->
                <CompassPlainButton
                    v-if="isInline"
                    :label="$t.__('common.labels.smart_tags')"
                    variant="neutral"
                    leftIcon="edit"
                    class="tag-save"
                    data-bot="smart-tags__link"
                    @click="editTags"
                />
            </div>
        </template>
    </div>
</template>

<script>
import LinkButton from 'vcomponents/utilities/LinkButton.vue';

export default {
    components: {
        'link-button': LinkButton,
    },
    props: {
        linkedtype: {
            type: String,
            required: true,
        },
        linkedid: {
            type: Number,
            required: false,
            default: null,
        },
        options: {
            type: Array,
            required: false,
            default: () => [],
        },
        permissions: {
            type: [Boolean, Object],
            required: false,
            default: false,
        },
        //can be 'line', 'column' or 'inline'
        display: {
            type: String,
            required: false,
            default: 'column',
        },
        edit: {
            type: Boolean,
            required: false,
            default: false,
        },
        standalone: {
            type: Boolean,
            required: false,
            default: true,
        },
        fetchTags: {
            type: Boolean,
            required: false,
            default: true,
        },
        size: {
            type: String,
            required: false,
            default: 'medium',
            validator: (value) => ['large', 'small', 'mini', 'medium'].includes(value),
        },
    },

    data() {
        return {
            editMode: false,
            tags: [],
            values: [],
            backupValues: [],
            loader: false,
            persistantOptions: [],
            permToCheck: 'can_edit',
        };
    },
    computed: {
        tagsStore() {
            return this.$store.getters.tags;
        },
        isInline() {
            return this.display === 'inline';
        },
        labelTagClass() {
            return this.isInline ? 'label-tag__label-as-inline' : 'mb-1';
        },
        labelTagContainerClass() {
            return this.isInline ? 'label-tag__container' : ' ai-center';
        },
        containerTagClass() {
            return this.isInline ? 'flex-row tags-selector__container ' : '';
        },
        selectorContainerClass() {
            return !this.isInline ? 'wp-10' : '';
        },
    },
    created() {
        this.editMode = this.edit;
    },
    mounted() {
        if (this.options.length > 0 || !this.fetchTags) {
            this.tags = this.options;
            this.feedValues();
        } else {
            this.fetch();
        }
        this.persistantOptions = [...this.tags];
    },

    methods: {
        editInPlace() {
            !this.isInline ? this.editTags() : false;
        },
        editTags() {
            this.permissions[this.permToCheck] ? this.toggleMode() : false;
        },
        toggleMode() {
            this.backupValues = this.values;
            this.persistantOptions = [...this.tagsStore];
            this.values = this.tagsStore.map((tag) => tag.id);
            this.tags = this.persistantOptions;
            this.editMode = !this.editMode;
        },

        fetch() {
            this.loader = true;
            this.$ajax.get(
                `/tags/${this.linkedtype}/${this.linkedid}`,
                {},
                (response) => {
                    this.tags = response;
                    this.persistantOptions = response;
                    this.feedValues();
                    this.loader = false;
                },
                () => {
                    Push.error(
                        this.$l10n.__(
                            'Une erreur est survenue lors de la récupération des tags. Veuillez ré-essayer dans quelques minutes.',
                        ),
                        false,
                    );
                },
            );
        },

        remote: _.debounce(function (query) {
            this.$ajax.get(
                `/tags/${this.linkedtype}`,
                { q: query },
                (response) => {
                    this.tags = response;
                    this.feedValues();
                },
                () => {
                    Push.error(
                        this.$l10n.__(
                            'Une erreur est survenue lors de la récupération des tags. Veuillez ré-essayer dans quelques minutes.',
                        ),
                        false,
                    );
                },
            );
        }, 100),

        feedValues() {
            this.tags = this.tags.map((tag) => {
                if (tag.active) {
                    this.values.push(tag.id);
                } else if (tag.linkedForm) {
                    this.values.push(tag.value);
                    this.update(this.values);
                }

                return { ...tag, tooltip: false };
            });

            this.activeTooltips();
        },

        activeTooltips() {
            if (this.editMode) return;

            this.$nextTick(() => {
                this.tags.forEach((tag, index) => {
                    tag.tooltip =
                        this.$refs.tags[index].$el.scrollWidth <
                        this.$refs.tags[index].$el.offsetWidth;
                });
            });
        },

        tagFromId(id) {
            return this.persistantOptions.find((tag) => tag.id == id);
        },

        update(newValue) {
            // new id
            let newTagId = '';
            newValue.map((val) => {
                const currentTag = this.persistantOptions.find((option) => option.id == val);
                if (!currentTag) {
                    newTagId = val;
                }
            });

            if (newTagId !== '') {
                const newTag = this.tags.find((tag) => tag.id == newTagId);
                if (newTag) {
                    newTag.active = true;
                    this.persistantOptions.push(newTag);
                }
            }

            if (!this.standalone) {
                this.$emit('tagsUpdated', this.prePost());
            }
        },
        prePost() {
            const tagsForPost = [];
            _.each(this.values, (item) => {
                const tagFound = this.tagFromId(item);
                if (tagFound) {
                    tagsForPost.push({
                        id: tagFound.id,
                        value: tagFound.value,
                    });
                } else {
                    tagsForPost.push({
                        id: null,
                        value: item,
                    });
                }
            });
            return tagsForPost;
        },
        record() {
            //axios query to record new values
            const valuesForPost = this.prePost();
            this.loader = true;
            this.$ajax.put(
                `/tags/${this.linkedtype}/${this.linkedid}`,
                {},
                {
                    tags: valuesForPost,
                },
                (response) => {
                    this.$store.dispatch('updateTags', response);
                    if (response?.length > 0) {
                        Push.success(
                            this.$l10n.__('Les informations ont bien été enregistrées.'),
                            false,
                        );
                    }
                    this.reset();
                    this.toggleMode();
                    this.loader = false;
                },
                () => {
                    Push.error(
                        this.$l10n.__(
                            "Une erreur est survenue lors de l'enregistrement des tags. Veuillez ré-essayer dans quelques minutes.",
                        ),
                        false,
                    );
                },
            );
        },

        cancel() {
            this.values = this.backupValues;
            this.editMode = !this.editMode;
        },

        reset() {
            this.backupValues = [];
        },
    },
};
</script>

<style scoped lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.label-tag__container {
    padding: 4px 0 0;

    .label-tag__label-as-inline {
        border: 1px solid $gray-40;
        padding: 3px 7px;
        background: initial;
        line-height: 16px;
        font-size: 12px;
    }
}

.tag-save {
    flex: none;
}

// Overwrite ElementUI
.tags-selector__container {
    align-items: start;
    .el-select {
        width: 520px;
    }
    ::v-deep .el-select__tags {
        padding-left: 12px;

        input[type='text']:not(.el-input__inner, .compass-input__inner) {
            padding-left: 0;
        }
        .el-tag.el-tag--info {
            background: initial;
            border-color: $gray-40;
            border-style: solid;
            border-width: 1px;
            color: $gray-100;
            padding: 3px 11px 3px 7px;
            margin: 4px 4px 4px 0;

            .el-select__tags-text {
                line-height: 16px;
                font-size: 12px;
            }
        }
        .el-tag__close:hover {
            background-color: initial;
        }
        .el-icon-close {
            margin-left: 12px;
            background-color: initial;
            transform: unset;
            right: 0;
            height: 8px;
            width: 8px;
            line-height: initial;

            &:before {
                color: $gray-80;
                transform: unset;
                font-weight: 700;
                font-size: 11px;
                position: absolute;
                left: -2px;
                top: -1px;
            }
        }
    }
}
</style>

<style lang="scss">
.label-tag--tooltip__container {
    max-width: 480px;
}
</style>
